import { z } from 'zod';

// ─── Primitive IDs ────────────────────────────────────────────────────────────

export type WorkspaceId = string;
export type CapabilityId = string;
export type NodeId = string;
export type EdgeId = string;
export type CollectionId = string;
export type PluginId = string;
export type UserId = string;

// ─── Enums ────────────────────────────────────────────────────────────────────

export const CapabilityType = {
  SKILL: 'skill',
  KNOWLEDGE: 'knowledge',
  EXPERIENCE: 'experience',
  TOOL: 'tool',
  RELATIONSHIP: 'relationship',
  CREDENTIAL: 'credential',
  DOMAIN: 'domain',
} as const;

export type CapabilityType = typeof CapabilityType[keyof typeof CapabilityType];

export const CapabilityStatus = {
  ACTIVE: 'active',
  DORMANT: 'dormant',
  GROWING: 'growing',
  DEPRECATED: 'deprecated',
} as const;

export type CapabilityStatus = typeof CapabilityStatus[keyof typeof CapabilityStatus];

export const EdgeType = {
  ENABLES: 'enables',
  REQUIRES: 'requires',
  RELATED_TO: 'related_to',
  PART_OF: 'part_of',
  LEADS_TO: 'leads_to',
  CONFLICTS_WITH: 'conflicts_with',
} as const;

export type EdgeType = typeof EdgeType[keyof typeof EdgeType];

export const SyncStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  CONFLICT: 'conflict',
  ERROR: 'error',
} as const;

export type SyncStatus = typeof SyncStatus[keyof typeof SyncStatus];

export const SyncOperation = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

export type SyncOperation = typeof SyncOperation[keyof typeof SyncOperation];

export const AIResultType = {
  SUGGESTION: 'suggestion',
  SEARCH: 'search',
  EXTRACTION: 'extraction',
  COACHING: 'coaching',
  SUMMARY: 'summary',
  CLUSTER: 'cluster',
} as const;

export type AIResultType = typeof AIResultType[keyof typeof AIResultType];

export const PluginStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ERROR: 'error',
} as const;

export type PluginStatus = typeof PluginStatus[keyof typeof PluginStatus];

// ─── Core Domain Types ────────────────────────────────────────────────────────

export interface User {
  id: UserId;
  email: string;
  name: string;
  avatarUrl: string | null;
  bio: string | null;
  createdAt: string;
  updatedAt: string;
  preferences: UserPreferences;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  defaultGraphLayout: GraphLayout;
  emailNotifications: boolean;
  aiSuggestionsEnabled: boolean;
  autoSyncEnabled: boolean;
  sidebarCollapsed: boolean;
}

export const GraphLayout = {
  FCOSE: 'fcose',
  DAGRE: 'dagre',
  COLA: 'cola',
  CONCENTRIC: 'concentric',
  GRID: 'grid',
  CIRCLE: 'circle',
} as const;

export type GraphLayout = typeof GraphLayout[keyof typeof GraphLayout];

export interface Workspace {
  id: WorkspaceId;
  name: string;
  slug: string;
  description: string | null;
  iconEmoji: string | null;
  coverUrl: string | null;
  ownerId: UserId;
  memberIds: UserId[];
  isPersonal: boolean;
  createdAt: string;
  updatedAt: string;
  settings: WorkspaceSettings;
}

export interface WorkspaceSettings {
  defaultCapabilityType: CapabilityType;
  aiProvider: 'openai' | 'anthropic' | 'local' | null;
  enablePublicSharing: boolean;
  enableVersionHistory: boolean;
}

export interface Capability {
  id: CapabilityId;
  workspaceId: WorkspaceId;
  title: string;
  summary: string | null;
  content: string; // TipTap JSON serialised as string
  type: CapabilityType;
  status: CapabilityStatus;
  tags: string[];
  strength: number; // 0–100 proficiency/confidence rating
  evidence: EvidenceItem[];
  collectionIds: CollectionId[];
  createdAt: string;
  updatedAt: string;
  createdBy: UserId;
  metadata: CapabilityMetadata;
  syncStatus?: SyncStatus;
}

export interface EvidenceItem {
  id: string;
  label: string;
  url: string | null;
  type: 'project' | 'certification' | 'publication' | 'course' | 'note';
  date: string | null;
}

export interface CapabilityMetadata {
  yearsExperience?: number;
  lastUsed?: string;
  industry?: string;
  source?: string;
  embedding?: number[]; // vector embedding for semantic search
}

export interface CapabilityNode {
  id: NodeId;
  capabilityId: CapabilityId;
  workspaceId: WorkspaceId;
  position: { x: number; y: number };
  size: number; // visual weight in the graph
  color: string | null; // override colour
  locked: boolean; // pin position in graph
  createdAt: string;
  updatedAt: string;
}

export interface Edge {
  id: EdgeId;
  workspaceId: WorkspaceId;
  sourceNodeId: NodeId;
  targetNodeId: NodeId;
  type: EdgeType;
  label: string | null;
  weight: number; // 0–1 relationship strength
  bidirectional: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Collection {
  id: CollectionId;
  workspaceId: WorkspaceId;
  name: string;
  description: string | null;
  iconEmoji: string | null;
  coverUrl: string | null;
  capabilityIds: CapabilityId[];
  parentId: CollectionId | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: UserId;
}

export interface Plugin {
  id: PluginId;
  workspaceId: WorkspaceId;
  name: string;
  description: string;
  version: string;
  author: string;
  iconUrl: string | null;
  status: PluginStatus;
  permissions: PluginPermission[];
  config: Record<string, unknown>;
  installedAt: string;
  updatedAt: string;
}

export type PluginPermission =
  | 'read:capabilities'
  | 'write:capabilities'
  | 'read:graph'
  | 'write:graph'
  | 'read:collections'
  | 'ai:invoke'
  | 'sync:push'
  | 'sync:pull';

// ─── Sync ─────────────────────────────────────────────────────────────────────

export interface SyncEvent {
  id: string;
  workspaceId: WorkspaceId;
  entityType: 'capability' | 'node' | 'edge' | 'collection';
  entityId: string;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  status: SyncStatus;
  retries: number;
  error: string | null;
  createdAt: string;
  syncedAt: string | null;
}

// ─── AI ───────────────────────────────────────────────────────────────────────

export interface AIResult {
  id: string;
  type: AIResultType;
  query: string | null;
  results: AIResultItem[];
  metadata: {
    model: string;
    processingMs: number;
    tokenCount: number;
  };
  createdAt: string;
}

export interface AIResultItem {
  id: string;
  capabilityId: CapabilityId | null;
  title: string;
  summary: string;
  score: number; // 0–1 relevance
  reasoning: string | null;
  suggestedAction: string | null;
  tags: string[];
}

export interface AICapabilityExtraction {
  title: string;
  type: CapabilityType;
  summary: string;
  tags: string[];
  strength: number;
  evidence: EvidenceItem[];
}

// ─── Activity / Feed ──────────────────────────────────────────────────────────

export interface ActivityEvent {
  id: string;
  workspaceId: WorkspaceId;
  userId: UserId;
  action:
    | 'capability.created'
    | 'capability.updated'
    | 'capability.deleted'
    | 'edge.created'
    | 'edge.deleted'
    | 'collection.created'
    | 'collection.updated'
    | 'plugin.installed'
    | 'workspace.updated';
  entityType: string;
  entityId: string;
  entityTitle: string;
  createdAt: string;
  meta: Record<string, unknown>;
}

// ─── Zod Schemas ─────────────────────────────────────────────────────────────

export const capabilitySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  summary: z.string().max(500).nullable(),
  content: z.string(),
  type: z.enum([
    'skill',
    'knowledge',
    'experience',
    'tool',
    'relationship',
    'credential',
    'domain',
  ]),
  status: z.enum(['active', 'dormant', 'growing', 'deprecated']),
  tags: z.array(z.string()),
  strength: z.number().min(0).max(100),
  evidence: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      url: z.string().nullable(),
      type: z.enum(['project', 'certification', 'publication', 'course', 'note']),
      date: z.string().nullable(),
    })
  ),
  collectionIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  metadata: z.record(z.unknown()),
});

export const createCapabilitySchema = capabilitySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});

export type CreateCapabilityInput = z.infer<typeof createCapabilitySchema>;
export type UpdateCapabilityInput = Partial<CreateCapabilityInput>;

export const edgeSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  sourceNodeId: z.string(),
  targetNodeId: z.string(),
  type: z.enum([
    'enables',
    'requires',
    'related_to',
    'part_of',
    'leads_to',
    'conflicts_with',
  ]),
  label: z.string().nullable(),
  weight: z.number().min(0).max(1),
  bidirectional: z.boolean(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const createEdgeSchema = edgeSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateEdgeInput = z.infer<typeof createEdgeSchema>;

// ─── Graph ────────────────────────────────────────────────────────────────────

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface GraphNode {
  id: NodeId;
  data: {
    label: string;
    type: CapabilityType;
    status: CapabilityStatus;
    strength: number;
    capabilityId: CapabilityId;
    tags: string[];
  };
  position: { x: number; y: number };
}

export interface GraphEdge {
  id: EdgeId;
  source: NodeId;
  target: NodeId;
  data: {
    type: EdgeType;
    label: string | null;
    weight: number;
    bidirectional: boolean;
  };
}

// ─── API responses ────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}
